import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatbotApi, ChatbotApiError } from './chatbotApi';
import { ConversationSummary } from './types';

const HISTORY_KEY = ['chatbot', 'conversations'] as const;

/**
 * History sidebar data layer.
 *
 * NOTE: JWT is temporarily disabled — the backend serves a shared "public"
 * history bucket when no token is present, so the list always loads (no
 * sign-in gate). Re-enable by restoring `enabled: !!getAuthToken()` and the
 * `authed` flag below. Rename/delete stay optimistic with rollback on failure.
 */
export function useConversationHistory() {
  const queryClient = useQueryClient();
  const authed = true; // JWT disabled — history is public for now.

  const query = useQuery<ConversationSummary[]>({
    queryKey: HISTORY_KEY,
    queryFn: ({ signal }) => chatbotApi.listConversations(signal),
    enabled: true,
    staleTime: 30_000,
    // Survive navigation: keep the cached list for 30 min after the sidebar
    // unmounts (default gcTime is only 5 min) so leaving the page and coming
    // back doesn't cold-fetch and blank the history. (#history-stale)
    gcTime: 30 * 60 * 1000,
    retry: (count, err) =>
      // Don't retry auth failures.
      !(err instanceof ChatbotApiError && (err.status === 401 || err.status === 403)) &&
      count < 1,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
  }, [queryClient]);

  const err = query.error as ChatbotApiError | null;
  // A lapsed/absent JWT surfaces as 401/403 on the (auth-required) history
  // endpoint. Distinguish it from "no conversations yet" so the sidebar can
  // prompt a sign-in rather than silently showing an empty list "after a while".
  const authExpired =
    err instanceof ChatbotApiError && (err.status === 401 || err.status === 403);

  const renameMutation = useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      chatbotApi.renameConversation(sessionId, title),
    onMutate: async ({ sessionId, title }) => {
      await queryClient.cancelQueries({ queryKey: HISTORY_KEY });
      const prev = queryClient.getQueryData<ConversationSummary[]>(HISTORY_KEY);
      queryClient.setQueryData<ConversationSummary[]>(HISTORY_KEY, (old) =>
        (old ?? []).map((c) => (c.sessionId === sessionId ? { ...c, title } : c))
      );
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(HISTORY_KEY, ctx.prev);
    },
    onSettled: () => refresh(),
  });

  const deleteMutation = useMutation({
    mutationFn: (sessionId: string) => chatbotApi.deleteConversation(sessionId),
    onMutate: async (sessionId) => {
      await queryClient.cancelQueries({ queryKey: HISTORY_KEY });
      const prev = queryClient.getQueryData<ConversationSummary[]>(HISTORY_KEY);
      queryClient.setQueryData<ConversationSummary[]>(HISTORY_KEY, (old) =>
        (old ?? []).filter((c) => c.sessionId !== sessionId)
      );
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(HISTORY_KEY, ctx.prev);
    },
    onSettled: () => refresh(),
  });

  return {
    conversations: query.data ?? [],
    isLoading: query.isLoading && authed,
    isError: query.isError,
    error: err,
    authed,
    authExpired,
    refresh,
    retry: () => void query.refetch(),
    rename: (sessionId: string, title: string) =>
      renameMutation.mutateAsync({ sessionId, title }),
    remove: (sessionId: string) => deleteMutation.mutateAsync(sessionId),
    renaming: renameMutation.isPending,
    removing: deleteMutation.isPending,
  };
}
